import React, { useEffect, useState, useRef } from 'react';
import HRAdminWorkflows from './CreateEmployee/HRAdminWorkflows';
import { useCompany } from '../../../hooks/useCompany';
import { useHRAdminEmployees } from '../../../hooks/useHRAdminEmployees';
import { useTranslation } from 'react-i18next';
import { showMessage } from 'react-native-flash-message';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useTheme } from '@react-navigation/native';
import { createCommonStyles } from '../../../styles/commonStyles';
import { useWorkflowsForCompany } from '../../../hooks/useWorkflowsForCompany';

const HRAdminEmplWorkflows = ({
  selectedEmployee,
  selectedCompany,
  onSuccess,
  changeStep
}) => {
  const { workflows, workflowObjs, loading: isLoadingWorkflows } = useWorkflowsForCompany(selectedCompany?.code);
  const childRef = useRef();
  const [defaultWorkflows, setDefaultWorkflows] = useState([]);
  const [originalWorkflows, setOriginalWorkflows] = useState([]);
  const { getEmployeeWorkflows, insertEmplWorkflow, deleteEmplWorkflows } = useHRAdminEmployees();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const common = createCommonStyles(colors);
  const styles = makeStyles(colors);
  const [isLoadingEmployeeWorkflows, setIsLoadingEmployeeWorkflows] = useState(false);

  useEffect(() => {
    const fetchEmployeeWorkflows = async () => {
      if (selectedEmployee?.employeeID && selectedCompany?.code) {
        setIsLoadingEmployeeWorkflows(true);
        try {
          const response = await getEmployeeWorkflows({
            employeeId: selectedEmployee.employeeID,
            company: selectedCompany.code,
          });
          if (response && response.status === 200) {
            const data = response.data || [];
            const mappedFields = data.map(item => ({
              workflowID: item.workflowId,
              rowguid: item.rowguid,
              employeeID: selectedEmployee.employeeID,
              company: selectedCompany.code,
            }));
            setDefaultWorkflows(mappedFields);
            setOriginalWorkflows(mappedFields);
          } else {
            setDefaultWorkflows([]);
            setOriginalWorkflows([]);
          }
        } catch (error) {
          console.error("Error fetching employee workflows:", error);
          setDefaultWorkflows([]);
          setOriginalWorkflows([]);
        } finally {
          setIsLoadingEmployeeWorkflows(false);
        }
      } else {
        setDefaultWorkflows([]);
        setOriginalWorkflows([]);
      }
    };
    fetchEmployeeWorkflows();
  }, [selectedEmployee, selectedCompany]);


  const handleCancelChanges = () => {
    setDefaultWorkflows([...originalWorkflows]);
    showMessage({
      message: t("common:changesCancelled"),
      type: "info",
      duration: 3000,
    });
  };

  const handleEditPress = (index, item, field, value) => {
    setDefaultWorkflows(prev => {
      const updated = [...prev];
      if (updated[index]) {
        updated[index] = {
          ...updated[index],
          [field]: value,
        };
      }
      return updated;
    });
  };

  const handleSubmitWorkflows = async (data) => {
    if (!selectedEmployee?.employeeID || !selectedCompany?.code) return;

    const employeeID = selectedEmployee.employeeID;
    const company = selectedCompany.code;
    const fromWorkflows = data?.workflows || [];
    const existing = originalWorkflows || [];
    // const deletedRowGuids = existing
    //   .filter((orig) => !fromWorkflows.some((wf) => wf.rowguid === orig.rowguid))
    //   .map((orig) => orig.rowguid)
    //   .filter(Boolean);
    const deletedRowGuids = existing
      .filter(orig => !fromWorkflows.some(wf => wf.rowguid === orig.rowguid))
      .map(orig => orig.rowguid)
      .filter(guid => typeof guid === 'string' && guid.trim() !== '');
    const prepareWorkflows = fromWorkflows.map((wf) => ({
      ...wf,
      employeeID,
      company,
    }));

    setIsLoadingEmployeeWorkflows(true);
    try {
      if (deletedRowGuids.length > 0) {
        const deleteResult = await deleteEmplWorkflows(deletedRowGuids);
        if (!deleteResult) {
          showMessage({
            message: t('common:issueMessage'),
            type: "error",
            duration: 3000,
          });
        } else {
          showMessage({
            message: t('common:deleteSuccessMessage'),
            type: "success",
            duration: 3000,
          });
        }
      }
      const res = await insertEmplWorkflow({
        employeeID,
        company,
        workflows: prepareWorkflows,
      });
      if (res?.status === 200) {
        showMessage({
          message: t('common:saveSuccessMessage'),
          type: "success",
          duration: 3000,
        });
        setDefaultWorkflows(prepareWorkflows);
        setOriginalWorkflows(prepareWorkflows);

        onSuccess && onSuccess();
        changeStep && changeStep();
        
      } else {
        showMessage({
          message: t('common:errorMessage'),
          type: "error",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error saving employee workflows:', error);
      showMessage({
        message: t('common:errorMessage'),
        type: "error",
        duration: 3000,
      });
    } finally {
      setIsLoadingEmployeeWorkflows(false);
    }
  };


  //#region Loader
  if (isLoadingWorkflows || isLoadingEmployeeWorkflows)
    return (
      <View style={styles.loader}>
        <ActivityIndicator animating={true} color={colors.blue} />
      </View>
    )
  //#endregion

  return (
    <>
      <HRAdminWorkflows
        employee={{
          employeeID: selectedEmployee?.employeeID,
          company: selectedCompany?.code,
        }}
        ref={childRef}
        workflows={workflows}
        workflowObjs={workflowObjs}
        defaultWorkflows={defaultWorkflows}
        isLoading={isLoadingWorkflows || isLoadingEmployeeWorkflows}
        setIsLoading={setIsLoadingEmployeeWorkflows}
        insertEmplWorkflow={insertEmplWorkflow}
        onSubmit={handleSubmitWorkflows}
        handleCancelChanges={handleCancelChanges}
        handleEditPress={handleEditPress}
        onSuccess={onSuccess}
        changeStep={changeStep}
      />
    </>
  );
}

const makeStyles = (colors, width) => StyleSheet.create({
  loader: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '4%'
  },
});

export default HRAdminEmplWorkflows;